/**
 * BrandPanel — decorative hero panel used on Login & Register pages.
 * Pure CSS/SVG (no external image) so it scales crisply and matches
 * the app's identity: dark navy + gold "معيار" in elegant Arabic.
 */
export default function BrandPanel({ lang = "ar", strings, ribbon }) {
    const brand = strings?.brand ?? "معيار";
    const tag = strings?.brandTag ?? "Al Meyar • نظام محاسبة الذهب";
    const subtitle =
        strings?.brandSubtitle ?? "إدارة دفاتر حسابات العملاء بدقّة وأناقة احترافية.";
    return (
        <div className="relative h-full w-full overflow-hidden rounded-[28px] bg-[#0b1e33] text-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.45)]">
            {/* Warm radial glow center */}
            <div
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(ellipse at 50% 40%, rgba(180,142,75,0.22) 0%, rgba(180,142,75,0.08) 35%, rgba(11,30,51,0) 65%)",
                }}
            />

            {/* Arabesque dotted pattern — top-right */}
            <div
                className="pointer-events-none absolute inset-0 opacity-[0.22]"
                style={{
                    backgroundImage:
                        "radial-gradient(rgba(217,175,102,0.85) 1px, transparent 1.5px)",
                    backgroundSize: "14px 14px",
                    maskImage:
                        "radial-gradient(circle at 88% 8%, black 0%, black 16%, transparent 40%), radial-gradient(circle at 12% 92%, black 0%, black 14%, transparent 38%)",
                    WebkitMaskImage:
                        "radial-gradient(circle at 88% 8%, black 0%, black 16%, transparent 40%), radial-gradient(circle at 12% 92%, black 0%, black 14%, transparent 38%)",
                }}
            />

            {/* Octagonal gold frame */}
            <svg
                className="pointer-events-none absolute inset-0 h-full w-full"
                viewBox="0 0 600 800"
                preserveAspectRatio="none"
                aria-hidden="true"
            >
                <defs>
                    <linearGradient id="goldStroke" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#e6c680" />
                        <stop offset="50%" stopColor="#b48e4b" />
                        <stop offset="100%" stopColor="#8a6a2d" />
                    </linearGradient>
                </defs>
                {/* outer octagon */}
                <polygon
                    points="80,40 520,40 560,80 560,720 520,760 80,760 40,720 40,80"
                    fill="none"
                    stroke="url(#goldStroke)"
                    strokeWidth="1.5"
                    opacity="0.75"
                />
                {/* inner octagon */}
                <polygon
                    points="100,60 500,60 540,100 540,700 500,740 100,740 60,700 60,100"
                    fill="none"
                    stroke="url(#goldStroke)"
                    strokeWidth="0.75"
                    opacity="0.45"
                />
            </svg>

            {/* Content stack */}
            <div className="relative z-10 flex h-full flex-col items-center justify-center px-8 text-center">
                {/* Optional ribbon label (e.g. "تسجيل الدخول") shown above the brand */}
                {ribbon ? (
                    <div
                        className="mb-6 rounded-full border border-amber-400/40 bg-amber-300/15 px-4 py-1.5 text-sm font-bold uppercase tracking-[0.18em] text-amber-200 backdrop-blur"
                        data-testid="brand-panel-ribbon"
                    >
                        {ribbon}
                    </div>
                ) : null}

                {/* Top diamond ornament */}
                <Ornament className="mb-8 h-6 w-24 opacity-80" />

                {/* Brand name */}
                <h1
                    className="brand-meyar select-none"
                    aria-label={brand}
                    style={lang === "en" ? { fontFamily: "'Playfair Display', 'Cinzel', serif", letterSpacing: "0.06em" } : undefined}
                >
                    {brand}
                </h1>

                {/* Bottom diamond ornament */}
                <Ornament className="mt-8 h-6 w-24 rotate-180 opacity-80" />

                {/* Tagline chip */}
                <div className="mt-10 inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-white/5 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-amber-200/90 backdrop-blur">
                    {tag}
                </div>
                <p className="mt-3 max-w-xs text-sm leading-relaxed text-amber-50/70">
                    {subtitle}
                </p>
            </div>

            {/* Bottom gradient for depth */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/40 to-transparent" />
        </div>
    );
}

function Ornament({ className = "" }) {
    return (
        <svg
            className={className}
            viewBox="0 0 120 20"
            fill="none"
            aria-hidden="true"
        >
            <defs>
                <linearGradient id="ornGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#b48e4b" stopOpacity="0" />
                    <stop offset="50%" stopColor="#e6c680" stopOpacity="1" />
                    <stop offset="100%" stopColor="#b48e4b" stopOpacity="0" />
                </linearGradient>
            </defs>
            <line x1="0" y1="10" x2="50" y2="10" stroke="url(#ornGrad)" strokeWidth="1" />
            <line x1="70" y1="10" x2="120" y2="10" stroke="url(#ornGrad)" strokeWidth="1" />
            <g transform="translate(60 10)">
                <polygon points="0,-7 6,0 0,7 -6,0" fill="#e6c680" />
                <polygon points="0,-3 2.5,0 0,3 -2.5,0" fill="#0b1e33" />
            </g>
        </svg>
    );
}
